import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircleIcon, PrinterIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { verificationApi } from '../services/api';
import { Layout } from '../components/shared/Layout';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { Alert } from '../components/shared/Alert';

export function ResultPage() {
  const { id } = useParams<{ id: string }>();

  const { data: result, isLoading, error } = useQuery({
    queryKey: ['verification-result', id],
    queryFn: () => verificationApi.getResult(id!).then((res) => res.data),
    enabled: !!id,
  });

  const handlePrint = () => {
    window.print();
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <Link
          to="/dashboard"
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Link>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : error ? (
          <Alert type="error" message="Failed to load verification result" />
        ) : result ? (
          <div className="card print:shadow-none print:border-none">
            <div className="text-center mb-8">
              <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto" />
              <h1 className="text-2xl font-bold text-gray-900 mt-4">
                Verification Complete
              </h1>
              <p className="text-gray-600 mt-2">
                Marital status verified through the Department of Home Affairs
              </p>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-1">Current Marital Status</p>
                <p className="text-3xl font-bold text-green-700">{result.maritalStatus}</p>
                {result.maritalStatusDate && (
                  <p className="text-sm text-gray-600 mt-2">
                    As of {new Date(result.maritalStatusDate).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <h2 className="font-medium text-gray-900 mb-4">Verification Details</h2>
              <dl className="space-y-3 text-sm">
                <div className="flex flex-col sm:flex-row sm:justify-between">
                  <dt className="text-gray-500">Full Name</dt>
                  <dd className="font-medium text-gray-900">
                    {result.firstName} {result.lastName}
                  </dd>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between">
                  <dt className="text-gray-500">ID Number</dt>
                  <dd className="font-medium text-gray-900 font-mono">{result.idNumber}</dd>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between">
                  <dt className="text-gray-500">Verification Date</dt>
                  <dd className="font-medium text-gray-900">
                    {new Date(result.completedAt).toLocaleString()}
                  </dd>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between">
                  <dt className="text-gray-500">Reference</dt>
                  <dd className="font-medium text-gray-900">{result.id.slice(0, 8).toUpperCase()}</dd>
                </div>
              </dl>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 print:hidden">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> This verification result was obtained from the Department of
                Home Affairs and is valid as of the date shown. For official purposes, you may need
                to obtain a certified marriage certificate from Home Affairs.
              </p>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs text-gray-500 mb-6">
              <p className="font-medium mb-2">POPIA Notice</p>
              <p>
                In accordance with the Protection of Personal Information Act (POPIA), your personal
                information used for this verification will be securely deleted 30 days after the
                completion of this request. Audit logs may be retained for compliance purposes.
              </p>
            </div>

            <div className="flex justify-center print:hidden">
              <button onClick={handlePrint} className="btn-secondary inline-flex items-center">
                <PrinterIcon className="h-5 w-5 mr-2" />
                Print Result
              </button>
            </div>
          </div>
        ) : (
          <Alert type="info" message="No result available for this verification" />
        )}
      </div>
    </Layout>
  );
}
